/**
 * StandDetayPage.jsx — Standlar > Stand Detayı (denetçi/staff)
 * Route: /Management/Standlar/:standId
 *
 * 2026-09-15: Standlar listesinin drill-down hedefi. Proje detayından
 * (/Management/Projects/Stands/:id — beyan, belgeler, revizyonlar) BİLEREK ayrı:
 * burası STANDIN kendisi — künye, firmalar, stand_detail bayrakları, borç, talepler,
 * konum. Görsel dil Firma tarafının "Standlarım" detayından (FirmaStandOverviewPage):
 * renkli stand rozetli başlık kartı + hap sekmeler + şeritli kartlar. Firma tarafı
 * salt-okunur; bu sayfa aynı bilgiyi YÖNETİR (firma ata/kaldır, eşleştirme talepleri,
 * durum bayrakları, borç).
 *
 * Veri: services/standDetayService.js (canlı, mock yok). Proje özeti tek istek, talepler
 * sekme açılınca. Sayfada backend notu/uyarı bandı yok — tuzaklar servis yorumlarında ve
 * util/knowledgebase/stand-detay-sayfasi.md'de.
 *
 * Backlog #70 (2026-10-08): başlıkta yalnızca 'Proje Detayı' kaldı. WAS: İnşa İzni, İş İzni, Borç
 * ve üç Taahhütname durum kareleri. İnşa İzni + Taahhütnameler sayfada yalnızca o karelerdeydi;
 * Durum & Borç'un başına salt okunur bir karta (InsaIzniCard) taşındı. İş İzni Talepler & İşler'de,
 * Borç Durum & Borç'ta zaten var. Mükerrer bilgi listesinin Deniz'le gözden geçirilmesi açık.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Container, Card, CardBody, CardHeader, Row, Col, Badge, Button, Nav, NavItem, NavLink,
  Spinner, Alert, Table, Input, Label, Modal, ModalHeader, ModalBody, ModalFooter, ButtonGroup,
} from "reactstrap";
// WAS: ..., Hammer, ClipboardCheck, ClipboardList, Coins, ... — ClipboardCheck ve Coins yalnızca
// #70'te kaldırılan başlık durum karelerindeydi.
import {
  ArrowLeft, Wrench, Hammer, ClipboardList, Layers3, Package, Building2, Maximize2,
  Ruler, Zap, Pencil, Trash2, Plus, FolderOpen, Image as ImageIcon, FileText,
  StickyNote, Ban, AlertTriangle, HardHat,
} from "lucide-react";
import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import {
  getStandDetay, updateStandDetail, setStandBorc, removeFirmaFromStand,
  STAND_FLAGS, STAND_DEBT_FLAGS, STAND_TEXT_FIELDS, fetchStandFotografi,
} from "../../../../services/standDetayService";
import {
  updateStand, assignFirmaToStand, getSalonOptions, COMMITMENT_STATE,
} from "../../../../services/standOverviewService";
import {
  getTeknikTalepler, getHizmetTalepleri, getIsIzinleri, getEkipmanTalebi,
  TEKNIK_TALEP_KATEGORILERI, HIZMET_TALEP_KATEGORILERI, IS_IZNI_KATEGORILERI, TALEP_STATUS,
  BORC_DURUMU_KATEGORILERI, HIZMET_BORC_META, BORC_DURUMU_STATUS,
} from "../../../../services/standTalepService";
import {
  getProjectByStandId, FILE_APPROVAL_STATUS, isRevisionUnfinished, getProjectFileUrl,
} from "../../../../services/projectService";
import { FIRMA_TYPE } from "../../../../services/firmaAramaService";
// #324: "Standdan kaldır" yalnızca stand.firma.manage yetkisi olan personele görünür.
import { useYetki, PERMISSION_KEYS } from "../../../../services/rbacService";
import KontrolluSilDugmesi from "../../../../Components/Common/KontrolluSilDugmesi";
import { standService } from "../../../../services/standService";
import { standCodeFromName, isStandAdiCakismasi } from "../../../../services/standNaming";
import { getScopes, getAllFilledForms } from "../../../../services/sahaInspectionService";
import { getSubmissions as getTadilatSubmissions } from "../../../../services/tadilaTalepService";
// WAS: permitBadge, workPermitBadge, ..., InfoBox, VDivider, ... — workPermitBadge ve VDivider
// yalnızca #70'te kaldırılan başlık durum karelerindeydi.
import {
  permitBadge, hasIssue, stripParallelTreeMarker,
  StatusTile, InfoBox, STATUS_HEX, NEUTRAL_GRAY, COMMITMENT_ICONS, TEKNIK_ICONS,
} from "../../../../Components/Projects/StandPills";
import StandIdentityFields from "../../../../Components/Projects/StandIdentityFields";
import ImzaSeviyesiRozeti from "../../../../Components/Projects/ImzaSeviyesiRozeti";
import { standBaslik } from "../../../../Components/Projects/standBaslik";
import FirmaSearchSelect from "../../../../Components/Firma/FirmaSearchSelect";
import FirmaAtamaTalepPanel from "../../../../Components/Firma/FirmaAtamaTalepPanel";
// #462 (2026-10-20): who entered a request (backend #454)
import TalepKaynagi from "../../../../Components/Talep/TalepKaynagi.jsx"; // .jsx written out: on a case-insensitive filesystem the bare name could also match a .js helper
// WAS (until #439): the Konum tab lived in this file and needed these three imports; it moved, unchanged for staff, into the shared
// Components/Kroki/StandKonumKarti.jsx so the company portal's stand pages use the same code (the old tab is kept below as a comment).
// import KrokiVectorEmbed from "../../../../Components/Kroki/KrokiVectorEmbed";
// import { resolveStandPlanDocument } from "../../../../services/krokiVectorService";
// import { getKonumMargins } from "../../../../services/systemSettingsService";
import StandKonumKarti from "../../../../Components/Kroki/StandKonumKarti";
// #453 (2026-10-17): the history of the stand's requests (backend #452)
import TalepGecmisi from "../../../../Components/Talep/TalepGecmisi.jsx"; // .jsx written out: on a case-insensitive filesystem the bare name also matches talepGecmisi.js (the helper, no default export)
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../../../i18n";

const LIST_PATH = "/Management/Standlar";

const TABS = [
  { key: "genel", get label() { return i18n.t("moduller:standDetay.standBilgileri"); } },
  { key: "firmalar", get label() { return i18n.t("moduller:standDetay.firmalar"); } },
  { key: "durum", get label() { return i18n.t("moduller:standDetay.durumBorc"); } },
  { key: "talepler", get label() { return i18n.t("moduller:standDetay.taleplerIsler"); } },
  { key: "konum", get label() { return i18n.t("moduller:standDetay.konum"); } },
];

const COMMITMENT_GROUPS = [
  { key: "isg", get label() { return i18n.t("moduller:standDetay.isgTaahhutnamesi"); } },
  { key: "statik", get label() { return i18n.t("moduller:standDetay.statikTaahhutnamesi"); } },
  { key: "elektrik", get label() { return i18n.t("moduller:standDetay.elektrikTaahhutnamesi"); } },
];
const BORC_KATEGORILERI = [...BORC_DURUMU_KATEGORILERI, HIZMET_BORC_META];
// Beyan aşaması — StandDurumuModal ile aynı sıra.
function beyanBadge(pb) {
  if (!pb) return null;
  if (!pb.declared) return { label: i18n.t("moduller:standDetay.beyanBekleniyor"), color: "secondary" };
  if (pb.locked && !pb.overridden) return { label: i18n.t("moduller:standDetay.kilitliKuralIhlali"), color: "danger" };
  if (pb.awaitingSystemAssignment) return { label: i18n.t("moduller:standDetay.onayBekliyor"), color: "warning" };
  if (pb.systemAssigned) return { label: i18n.t("moduller:standDetay.kabulEdildi"), color: "success" };
  return { label: i18n.t("moduller:standDetay.beyanEdildi"), color: "info" };
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString(currentLocaleTag(), { day: "2-digit", month: "short", year: "numeric" });
}

// Rozet — listedeki durum kareleriyle aynı hex palet (tema renkleri kayık, bkz. StandDurumuModal).
function HexBadge({ badge }) {
  if (!badge) return <span className="text-muted">—</span>;
  const hex = STATUS_HEX[badge.color];
  if (!hex) return <Badge color="light" className="text-muted border">{badge.label}</Badge>;
  return <span className="badge text-white" style={{ background: hex }}>{badge.label}</span>;
}

// Firma tarafındaki RequiredFileRibbon'un kopyası — şeritli başlıklı kart.
function RibbonCard({ label, actions, children, className = "" }) {
  return (
    <Card className={`position-relative overflow-hidden ${className}`}>
      <span
        className="badge bg-danger position-absolute text-white"
        style={{
          top: 12, left: -6, padding: "4px 16px 4px 10px",
          clipPath: "polygon(0 0, 100% 0, 88% 50%, 100% 100%, 0 100%)", fontSize: "0.7rem", zIndex: 2,
        }}
      >
        {label}
      </span>
      {actions && <div className="position-absolute top-0 end-0 p-2" style={{ zIndex: 2 }}>{actions}</div>}
      <CardBody className="pt-5">{children}</CardBody>
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <Col sm={6} md={4} className="mb-3">
      <div className="text-muted small">{label}</div>
      <div className="fw-semibold text-break">{children ?? <span className="text-muted fw-normal">—</span>}</div>
    </Col>
  );
}

// ─── Başlık ─────────────────────────────────────────────────────────────────
function StandHeader({ s, activeTab, onTab, onProject }) {
  const { t: tx } = useTranslation("moduller");
  const color = !s.firmaId ? NEUTRAL_GRAY : hasIssue(s) ? STATUS_HEX.danger : STATUS_HEX.success;
  const code = s.standCode || standCodeFromName(s.standName) || s.standName;
  const baslik = standBaslik(s);
  // WAS (#70'te durum kareleriyle birlikte kaldırıldı — İnşa İzni/Taahhütnameler artık Durum & Borç'ta):
  // const permit = permitBadge(s);
  // const work = workPermitBadge(s);
  const typeName = stripParallelTreeMarker(s.standTypeName);
  // const commitments = s.commitments || [];
  return (
    <Card className="mb-3 overflow-hidden">
      <div className="bg-light">
        <div className="p-4 d-flex align-items-center flex-wrap gap-4">
          <div
            className="position-relative rounded-3 text-white d-flex flex-column align-items-center justify-content-center shadow-sm px-2 flex-shrink-0"
            style={{ width: 84, height: 84, background: color }}
          >
            <span
              className="position-absolute top-0 start-0 translate-middle bg-dark text-white rounded-circle d-flex align-items-center justify-content-center"
              style={{ width: 24, height: 24 }}
            >
              <Wrench size={13} />
            </span>
            <div className={`fw-bold lh-1 text-truncate mw-100 ${code.length > 5 ? "fs-6" : "fs-4"}`}>{code}</div>
            {s.salonName && <div className="small lh-1 mt-1 text-truncate mw-100">{s.salonName}</div>}
          </div>

          <div className="flex-grow-1" style={{ minWidth: 220 }}>
            {/* #548: the stand's own name leads, then the firma's short and long names; no stand name = as before */}
            <h4 className="mb-1 text-truncate" title={baslik.tabelaVar ? tx("standDetay.standAdi") : undefined}>
              {baslik.birincil || <span className="text-muted fs-5">{tx("standDetay.katilimciAtanmamis")}</span>}
            </h4>
            {baslik.ikincil && <div className="text-muted small text-truncate">{baslik.ikincil}</div>}
            {s.firmaName && s.firmaName !== baslik.birincil && s.firmaName !== baslik.ikincil && <div className="text-muted small text-truncate">{s.firmaName}</div>}
            {s.dekoratorFirmaName ? (
              <div className="text-muted small"><Wrench size={11} className="me-1" />{s.dekoratorFirmaName}</div>
            ) : s.firmaId ? (
              <div className="text-danger small fw-semibold">{tx("standDetay.dekoratorAtanmamis")}</div>
            ) : null}
            <div className="text-muted small">
              {/* WAS (#208 öncesi): {s.standName}{s.tabelaAdi && <> · Tabela: <span className="fw-semibold text-body">{s.tabelaAdi}</span></>}
                  WAS (#208..#548): {s.tabelaAdi ? <><span className="fw-semibold text-body">{s.tabelaAdi}</span> · {s.standName}</> : s.standName} */}
              {s.standName}
            </div>
          </div>

          <div className="d-grid" style={{ gridTemplateColumns: "repeat(2, auto)", gap: "6px 8px" }}>
            {typeName && <InfoBox icon={Layers3} label={typeName} />}
            {s.floorInfo && <InfoBox icon={Building2} label={s.floorInfo} />}
            {s.standArea != null && <InfoBox icon={Maximize2} label={`${s.standArea} m²`} />}
            {s.projectHeightNumber != null && <InfoBox icon={Ruler} label={`${s.projectHeightNumber} m`} />}
            {s.kw != null && <InfoBox icon={Zap} label={`${s.kw} kW`} />}
          </div>

          {/* WAS (#70, Deniz 2026-09-17: başlıkta yalnızca 'Proje Detayı' kalsın). İnşa İzni ve üç
              Taahhütname Durum & Borç'taki InsaIzniCard'a taşındı; İş İzni Talepler & İşler'de,
              Borç Durum & Borç'ta zaten görünüyor.
          <div className="d-flex align-items-stretch flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              <StatusTile icon={Hammer} color={permit.color} opacity={STATUS_HEX[permit.color] ? 1 : 0.55} label={`İnşa İzni — ${permit.label}`} />
              <StatusTile icon={ClipboardCheck} color={work.color} opacity={STATUS_HEX[work.color] ? 1 : 0.55} label={`İş İzni — ${work.label}`} />
              <StatusTile
                icon={Coins} background={s.hasDebt ? STATUS_HEX.danger : STATUS_HEX.success}
                label={`Borç — ${s.hasDebt ? "Var" : "Yok"}`} onClick={() => onTab("durum")}
              />
            </div>
            <VDivider className="d-none d-xxl-block" />
            <div className="d-flex align-items-center gap-2">
              {COMMITMENT_GROUPS.map((g) => {
                const c = commitments.find((x) => x.key === g.key);
                const st = c?.state ? COMMITMENT_STATE[c.state] : null;
                return (
                  <StatusTile
                    key={g.key} icon={COMMITMENT_ICONS[g.key] ?? HardHat} color={st?.color}
                    opacity={st && STATUS_HEX[st.color] ? 1 : 0.55} label={`${g.label} — ${st?.label ?? "Kayıt yok"}`}
                  />
                );
              })}
            </div>
          </div>
          */}

          <div className="ms-xl-auto">
            <Button color="info" outline size="sm" onClick={onProject}>
              <FolderOpen size={14} className="me-1" />{tx("standDetay.projeDetayi")}
            </Button>
          </div>
        </div>

        <Nav pills className="px-4 gap-2 border-0">
          {TABS.map((t) => (
            <NavItem key={t.key}>
              <NavLink
                active={activeTab === t.key}
                className={activeTab === t.key ? "bg-danger text-white" : "bg-white text-dark"}
                style={{ cursor: "pointer", borderRadius: "6px 6px 0 0" }}
                onClick={() => onTab(t.key)}
              >
                {t.label}
              </NavLink>
            </NavItem>
          ))}
        </Nav>
      </div>
    </Card>
  );
}

// ─── Stand Bilgileri ────────────────────────────────────────────────────────
function ProjeOzetCard({ standId, onProject }) {
  const { t } = useTranslation("moduller");
  const [state, setState] = useState({ loading: true, project: null });
  useEffect(() => {
    let ignore = false;
    getProjectByStandId(standId, { forceLive: true })
      .then((p) => { if (!ignore) setState({ loading: false, project: p }); })
      .catch(() => { if (!ignore) setState({ loading: false, project: null }); });
    return () => { ignore = true; };
  }, [standId]);

  const p = state.project;
  const approvals = p?.fileApprovals || [];
  const approved = approvals.filter((a) => a.status === 1).length;
  const rejected = approvals.filter((a) => a.status === 2).length;
  const openRev = (p?.revisions || []).filter((r) => Array.isArray(r.messages) && isRevisionUnfinished(r)).length;

  return (
    <RibbonCard label={t("standDetay.proje")} className="h-100">
      {state.loading ? (
        <div className="text-center py-3"><Spinner size="sm" color="primary" /></div>
      ) : !p ? (
        <div className="text-muted small py-2">{t("standDetay.projeKaydiAcilmamis")}</div>
      ) : (
        <div className="vstack gap-2 small">
          <div className="d-flex justify-content-between"><span className="text-muted">{t("standDetay.projeBeyani")}</span><HexBadge badge={beyanBadge(p.projeBeyani)} /></div>
          <div className="d-flex justify-content-between">
            <span className="text-muted">{t("standDetay.belgeler")}</span>
            <span className="fw-semibold">
              {t("standDetay.onayli", { approved, length: approvals.length })}
              {rejected > 0 && <span className="text-danger ms-1">{t("standDetay.red", { rejected })}</span>}
            </span>
          </div>
          <div className="d-flex justify-content-between">
            <span className="text-muted">{t("standDetay.acikRevizyon")}</span>
            <HexBadge badge={openRev > 0 ? { label: String(openRev), color: "danger" } : { label: t("standDetay.yok"), color: "success" }} />
          </div>
          {p.standTypeName && (
            <div className="d-flex justify-content-between"><span className="text-muted">{t("standDetay.projeTuru")}</span><span className="fw-semibold">{stripParallelTreeMarker(p.standTypeName)}</span></div>
          )}
        </div>
      )}
      <Button color="info" outline size="sm" className="mt-3 w-100" onClick={onProject}>{t("standDetay.projeDetayinaGit")}</Button>
    </RibbonCard>
  );
}

// Denetim formları — backlog #71: hiçbir staff stand sayfası bunu göstermiyordu
// ("knowledgebase says that was deliberate" — talep artık bunun değişmesini istiyor).
// scopeTargetId TEK BAŞINA hedef tipini ayırmaz (bkz. sahaInspectionService.js yorumu),
// bu yüzden dönen satırlar scopeMap'in target_entity'sine göre burada süzülüyor —
// FormInstancesPage.jsx'teki aynı desen. Tür adı (inspectionTypeId) ÇÖZÜLMÜYOR — her
// olası scope için ayrı bir getTypes çağrısı gerektirirdi; kapsam dışı bırakıldı, seviye
// (Denetim Raporu/Uygunsuzluk/Kapalı DÖF) + tarih bu ilk geçiş için yeterli.
const INSPECTION_LEVEL_LABELS = { get 1() { return i18n.t("moduller:standDetay.denetimRaporu"); }, get 2() { return i18n.t("moduller:standDetay.uygunsuzlukDof"); }, get 3() { return i18n.t("moduller:standDetay.kapaliDof"); } };
function InspectionFormsCard({ standId, fuarId }) {
  const { t } = useTranslation("moduller");
  const [state, setState] = useState({ loading: true, forms: [] });
  useEffect(() => {
    let ignore = false;
    Promise.all([
      getScopes(),
      getAllFilledForms({ fuarId, scopeTargetId: standId, limit: 200 }),
    ]).then(([scopes, forms]) => {
      if (ignore) return;
      const standScopeIds = new Set(
        scopes.filter((sc) => sc.target_entity === "stand").map((sc) => sc.inspection_scope_id)
      );
      const filtered = (forms || []).filter(
        (f) => standScopeIds.has(f.inspectionScopeId) && f.scopeTargetId === Number(standId)
      );
      setState({ loading: false, forms: filtered });
    }).catch(() => { if (!ignore) setState({ loading: false, forms: [] }); });
    return () => { ignore = true; };
  }, [standId, fuarId]);

  if (state.loading) {
    return <RibbonCard label={t("standDetay.denetimFormlari")} className="mb-3"><div className="text-center py-2"><Spinner size="sm" color="primary" /></div></RibbonCard>;
  }
  if (state.forms.length === 0) return null;

  return (
    <RibbonCard label={t("standDetay.denetimFormlari")} className="mb-3">
      <div className="vstack gap-2">
        {state.forms.map((f) => (
          <div key={f.id} className="d-flex align-items-center gap-2 small">
            <ClipboardList size={15} className="text-muted flex-shrink-0" />
            <span className="fw-semibold">{INSPECTION_LEVEL_LABELS[f.inspectionLevelId] || t("standDetay.seviye", { inspectionLevelId: f.inspectionLevelId })}</span>
            <span className="text-muted">{fmtDate(f.createdAt)}</span>
            {f.isDone && <Badge color="success-subtle" className="text-success">{t("standDetay.tamamlandi")}</Badge>}
          </div>
        ))}
      </div>
    </RibbonCard>
  );
}

// Proje belgeleri — ProjeOzetCard yalnızca sayıları gösteriyor; #71 "documents/files"
// bölümü gerçek dosyaları istiyor. Aynı fetch'i tekrarlamamak için ayrı bir istek —
// ProjeOzetCard'ın kendi state'i paylaşılmıyor, ikisi de küçük/ucuz aynı GET.
function ProjectDocumentsCard({ standId }) {
  const { t } = useTranslation("moduller");
  const [state, setState] = useState({ loading: true, files: [] });
  useEffect(() => {
    let ignore = false;
    getProjectByStandId(standId, { forceLive: true })
      .then((p) => { if (!ignore) setState({ loading: false, files: p?.uploadedFiles || [] }); })
      .catch(() => { if (!ignore) setState({ loading: false, files: [] }); });
    return () => { ignore = true; };
  }, [standId]);

  if (state.loading) {
    return <RibbonCard label={t("standDetay.belgeler")} className="mb-3"><div className="text-center py-2"><Spinner size="sm" color="primary" /></div></RibbonCard>;
  }
  if (state.files.length === 0) return null;

  return (
    <RibbonCard label={t("standDetay.belgeler")} className="mb-3">
      <div className="vstack gap-2">
        {state.files.map((file, i) => (
          <a
            key={file.id ?? file._id ?? i} href={getProjectFileUrl(file.storedFileName)} target="_blank" rel="noreferrer"
            className="d-flex align-items-center gap-2 small text-body text-decoration-none"
          >
            <FileText size={15} className="text-muted flex-shrink-0" />
            <span className="text-truncate">{file.originalFileName || file.storedFileName}</span>
          </a>
        ))}
      </div>
    </RibbonCard>
  );
}

// Tadilat talepleri — standId zaten native destekleniyor (diğer ikisinin aksine
// disambiguation gerekmiyor), en ucuz üçüncü bölüm.
function TadilatCard({ standId, fuarId }) {
  const { t } = useTranslation("moduller");
  const [state, setState] = useState({ loading: true, records: [] });
  useEffect(() => {
    let ignore = false;
    getTadilatSubmissions({ fuarId, standId, limit: 50 })
      .then((r) => { if (!ignore) setState({ loading: false, records: r?.records || [] }); })
      .catch(() => { if (!ignore) setState({ loading: false, records: [] }); });
    return () => { ignore = true; };
  }, [standId, fuarId]);

  if (state.loading || state.records.length === 0) return null;

  return (
    <RibbonCard label={t("standDetay.tadilatTalepleri")} className="mb-3">
      <div className="vstack gap-2">
        {state.records.map((r) => (
          <div key={r.id} className="d-flex align-items-center gap-2 small">
            <Hammer size={15} className="text-muted flex-shrink-0" />
            <span className="text-truncate">{r.tadilat?.yapilacakIs || t("standDetay.tadilatTalebi")}</span>
            <span className="text-muted ms-auto">{fmtDate(r.createdAt)}</span>
          </div>
        ))}
      </div>
    </RibbonCard>
  );
}

function GenelTab({ s, onEdit, onProject }) {
  const { t } = useTranslation("moduller");
  const heightM = s.projectHeightNumber ?? (s.detailHeightCm != null ? s.detailHeightCm / 100 : null);
  const typePath = [s.standCategory, s.floorInfo, s.projectType, s.projectStructure].filter(Boolean).join(" › ");
  return (
    <>
      <Row className="g-3 mb-3">
        <Col lg={8}>
          <RibbonCard
            label={t("standDetay.kunye")} className="h-100"
            actions={<Button color="light" size="sm" onClick={onEdit}><Pencil size={13} className="me-1" />{t("common:duzenle")}</Button>}
          >
            <Row>
              {/* #592: Salon / Stand Kodu / Stand No are no longer repeated here - the page header already shows them
                  (hall, code tile, full name); they are still edited in the Künye modal.
                  WAS: <Field label={t("standDetay.salon")}>{s.salonName}</Field>
                       <Field label={t("standDetay.standKodu")}>{s.standCode || standCodeFromName(s.standName)}</Field>
                       <Field label={t("standDetay.standNo")}>{s.standName}</Field> */}
              <Field label={t("standDetay.standAdi")}>{s.tabelaAdi}</Field>
              <Field label={t("standDetay.standTuru")}>{stripParallelTreeMarker(s.standTypeName)}</Field>
              <Field label={t("standDetay.turAgaci")}>{typePath || null}</Field>
              <Field label={t("standDetay.alan")}>
                {s.standArea != null ? `${s.standArea} m²` : null}
              </Field>
              <Field label={t("standDetay.yukseklik")}>{heightM != null ? `${heightM} m` : null}</Field>
              <Field label={t("standDetay.elektrik")}>{s.kw != null ? `${s.kw} kW` : null}</Field>
            </Row>
            {s.firms.some((f) => f.forbidden) || s.detailValues.isForbidden ? (
              <div className="small text-danger fw-semibold">
                <Ban size={13} className="me-1" />
                {s.detailValues.isForbidden ? t("standDetay.standYasakli") : t("standDetay.standdaYasakliFirmaVar")}
                {s.forbiddenNote && <span className="fw-normal text-muted"> — {s.forbiddenNote}</span>}
              </div>
            ) : null}
          </RibbonCard>
        </Col>
        <Col lg={4}>
          <ProjeOzetCard standId={s.standId} onProject={onProject} />
        </Col>
      </Row>

      <InspectionFormsCard standId={s.standId} fuarId={s.fuarId} />
      <ProjectDocumentsCard standId={s.standId} />
      <TadilatCard standId={s.standId} fuarId={s.fuarId} />

      {s.notes.length > 0 && (
        <RibbonCard label={t("standDetay.notlar")} className="mb-3">
          <div className="vstack gap-2">
            {s.notes.map((n) => (
              <div key={n.id} className="d-flex gap-2">
                <StickyNote size={15} className={n.primary ? "text-warning mt-1" : "text-muted mt-1"} />
                <div>
                  <div>{n.text}</div>
                  <div className="text-muted small">{fmtDate(n.date)}</div>
                </div>
              </div>
            ))}
          </div>
        </RibbonCard>
      )}

      {s.photos.length > 0 && (
        <RibbonCard label={t("standDetay.fotograflar")}>
          <Row className="g-3">
            {s.photos.map((p) => (
              <Col xs={6} md={3} xl={2} key={p.id}>
                <div className="border rounded-3 overflow-hidden h-100">
                  {/* Backlog #405: Java'nın fotoğraf ucu oturum ister; korumalı fotoğraflar blob ile okunur. */}
                  {p.url && p.korumali ? (
                    <KorumaliStandFotografi url={p.url} alt={p.name} />
                  ) : p.url ? (
                    <a href={p.url} target="_blank" rel="noreferrer">
                      <img src={p.url} alt={p.name} className="w-100" style={{ height: 110, objectFit: "cover" }} />
                    </a>
                  ) : (
                    <div className="bg-light d-flex align-items-center justify-content-center" style={{ height: 110 }}>
                      <ImageIcon size={28} className="text-muted" />
                    </div>
                  )}
                  <div className="p-2 small">
                    <div className="text-truncate" title={p.name}>{p.name}</div>
                    <div className="text-muted">{p.typeLabel ? `${p.typeLabel} · ` : ""}{fmtDate(p.date)}</div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </RibbonCard>
      )}
    </>
  );
}

// ─── Korumalı fotoğraf (backlog #405) ────────────────────────────────────────
// GET /betterStands/{fuarId}/{dosya} artık oturum ister; <img src> başlık gönderemediği için baytlar
// blob olarak okunur (useKisiFoto ile aynı yol). Okunana kadar ya da okunamazsa yer tutucu görünür.
function KorumaliStandFotografi({ url, alt }) {
  const [foto, setFoto] = useState(null); // { url, objectUrl } — hangi adresin baytları olduğu ile

  useEffect(() => {
    let iptal = false;
    let okunan = null;
    fetchStandFotografi(url)
      .then((u) => {
        if (iptal) { URL.revokeObjectURL(u); return; }
        okunan = u;
        setFoto({ url, objectUrl: u });
      })
      .catch(() => { if (!iptal) setFoto(null); });
    return () => {
      iptal = true;
      if (okunan) URL.revokeObjectURL(okunan);
    };
  }, [url]);

  // Adres değiştiyse eski fotoğraf gösterilmez; yenisi okunana kadar yer tutucu.
  const objectUrl = foto && foto.url === url ? foto.objectUrl : null;
  if (!objectUrl) {
    return (
      <div className="bg-light d-flex align-items-center justify-content-center" style={{ height: 110 }}>
        <ImageIcon size={28} className="text-muted" />
      </div>
    );
  }
  return (
    <a href={objectUrl} target="_blank" rel="noreferrer">
      <img src={objectUrl} alt={alt} className="w-100" style={{ height: 110, objectFit: "cover" }} />
    </a>
  );
}

// ─── Firmalar ───────────────────────────────────────────────────────────────
function FirmalarTab({ s, onChanged }) {
  const { t: tx } = useTranslation("moduller");
  const [firma, setFirma] = useState(null);
  const [typeId, setTypeId] = useState(FIRMA_TYPE.DEKORATOR);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  // #324: backend asks stand.firma.manage (WAS: groups 1 / 2); null while GET /rbac/me loads.
  const kaldirabilir = useYetki(PERMISSION_KEYS.STAND_FIRMA_MANAGE) === true;

  async function handleAssign() {
    if (!firma) return;
    setBusy(true);
    setMsg(null);
    try {
      await assignFirmaToStand(s.standId, { firmaId: firma.firmaId, firmaTypeId: typeId, fuarId: s.fuarId }, { forceLive: true });
      setMsg({ color: "success", text: tx("standDetay.atandi", { firmaName: firma.firmaName }) });
      setFirma(null);
      onChanged();
    } catch (e) {
      setMsg({ color: "danger", text: e?.response?.data?.message || e?.message || tx("standDetay.firmaAtanamadi") });
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove() {
    const target = removeTarget;
    setBusy(true);
    setMsg(null);
    try {
      await removeFirmaFromStand(target.relationId);
      setMsg({ color: "success", text: tx("standDetay.standdanKaldirildi", { firmaName: target.firmaName }) });
      onChanged();
    } catch (e) {
      setMsg({ color: "danger", text: e.message });
    } finally {
      setBusy(false);
      setRemoveTarget(null);
    }
  }

  const sorted = [...s.firms].sort((a, b) => (a.level - b.level) || (a.firmaTypeId - b.firmaTypeId));

  return (
    <>
      {msg && <Alert color={msg.color} className="py-2" toggle={() => setMsg(null)}>{msg.text}</Alert>}
      <Row className="g-3">
        <Col xl={7}>
          <RibbonCard label={tx("standDetay.standFirmalari")} className="h-100">
            {sorted.length === 0 ? (
              <div className="text-muted small">{tx("standDetay.buStandaBagliFirmaYok")}</div>
            ) : (
              <div className="table-responsive">
                <Table className="align-middle mb-0" size="sm">
                  <thead className="text-muted small">
                    <tr><th>{tx("standDetay.firma")}</th><th>{tx("standDetay.tur")}</th><th className="text-center">{tx("standDetay.seviye2")}</th><th /></tr>
                  </thead>
                  <tbody>
                    {sorted.map((f) => (
                      <tr key={f.relationId}>
                        <td>
                          <div className="fw-semibold">
                            {f.firmaShortName || f.firmaName}
                            {f.forbidden && <Badge color="danger" className="ms-2">{tx("standDetay.yasakli")}</Badge>}
                          </div>
                          {f.firmaShortName && <div className="text-muted small">{f.firmaName}</div>}
                        </td>
                        <td>{f.firmaTypeName}</td>
                        <td className="text-center">{f.level}</td>
                        <td className="text-end">
                          {kaldirabilir && (
                            <Button color="danger" outline size="sm" title={tx("standDetay.standdanKaldir")} disabled={busy} onClick={() => setRemoveTarget(f)}>
                              <Trash2 size={13} />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </RibbonCard>
        </Col>
        <Col xl={5}>
          <RibbonCard label={tx("standDetay.firmaAta")} className="h-100">
            <FirmaSearchSelect value={firma} onChange={setFirma} forceLive disabled={busy} />
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-3">
              <ButtonGroup size="sm">
                {[{ id: FIRMA_TYPE.KATILIMCI, label: tx("standDetay.katilimci") }, { id: FIRMA_TYPE.DEKORATOR, label: tx("standDetay.dekorator") }].map((t) => (
                  <Button key={t.id} color="primary" outline={typeId !== t.id} onClick={() => setTypeId(t.id)} disabled={busy}>
                    {t.label}
                  </Button>
                ))}
              </ButtonGroup>
              <Button color="success" size="sm" disabled={!firma || busy} onClick={handleAssign}>
                {busy ? <Spinner size="sm" className="me-1" /> : <Plus size={14} className="me-1" />}{tx("standDetay.ata")}
              </Button>
            </div>
          </RibbonCard>
        </Col>
      </Row>

      <RibbonCard label={tx("standDetay.eslestirmeTalepleri")} className="mt-3">
        <FirmaAtamaTalepPanel standId={s.standId} fuarId={s.fuarId} forceLive onChanged={onChanged} />
      </RibbonCard>

      <Modal isOpen={!!removeTarget} toggle={() => setRemoveTarget(null)} centered>
        <ModalHeader toggle={() => setRemoveTarget(null)}>{tx("standDetay.firmayiKaldir")}</ModalHeader>
        <ModalBody>
          <strong>{removeTarget?.firmaName}</strong>{" "}{tx("standDetay.buStanddanKaldirilsinMi", { firmaTypeName: removeTarget?.firmaTypeName })}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setRemoveTarget(null)}>{tx("standDetay.vazgec")}</Button>
          <Button color="danger" onClick={handleRemove} disabled={busy}>{tx("standDetay.kaldir")}</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

// ─── Durum & Borç ───────────────────────────────────────────────────────────
function SwitchRow({ id, label, checked, onChange, danger, disabled }) {
  return (
    <div className="form-check form-switch mb-2">
      <Input type="switch" id={id} className="form-check-input" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <label className={`form-check-label ${checked && danger ? "text-danger fw-semibold" : ""}`} htmlFor={id}>{label}</label>
    </div>
  );
}

// #70 (2026-10-08): İnşa İzni ve üç Taahhütname başlıktaki durum karelerinden buraya taşındı —
// sayfada başka hiçbir yerde görünmüyorlardı. Salt okunur: İnşa İzni elle verilmez, standın
// gerekli belgelerinin onay durumundan türetilir (Deniz, 2026-09-23; doğruluğu #152). #73 bu
// sekmeyi yeniden tasarlarken bu bloğu da kendi düzenine alabilir.
function InsaIzniCard({ s }) {
  const { t } = useTranslation("moduller");
  const permit = permitBadge(s);
  const commitments = s.commitments || [];
  return (
    <RibbonCard label={t("standDetay.insaIzniTaahhutnameler")}>
      <Row className="g-3">
        <Col sm={6} xl={3}>
          <div className="d-flex align-items-center gap-2">
            <Hammer size={16} className="text-muted flex-shrink-0" />
            <div>
              <div className="text-muted small">{t("standDetay.insaIzni")}</div>
              <HexBadge badge={permit} />
            </div>
          </div>
        </Col>
        {COMMITMENT_GROUPS.map((g) => {
          const c = commitments.find((x) => x.key === g.key);
          const st = c?.state ? COMMITMENT_STATE[c.state] : null;
          const Icon = COMMITMENT_ICONS[g.key] ?? HardHat;
          return (
            <Col sm={6} xl={3} key={g.key}>
              <div className="d-flex align-items-center gap-2">
                <Icon size={16} className="text-muted flex-shrink-0" />
                <div>
                  <div className="text-muted small">{g.label}</div>
                  <HexBadge badge={st ?? { label: t("standDetay.kayitYok") }} />
                  {/* #506: the Elektrik one also says who signed (decorator / decorator + electrician). */}
                  <ImzaSeviyesiRozeti level={c?.level} elektrikciName={c?.elektrikciName} className="mt-1" />
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
      <div className="text-muted fs-12 mt-3">
        {t("standDetay.saltOkunurInsaIzniStandin")}
      </div>
    </RibbonCard>
  );
}

function DurumTab({ s, onChanged }) {
  const { t } = useTranslation("moduller");
  const [values, setValues] = useState(s.detailValues);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const dirty = useMemo(
    () => Object.keys(values).some((k) => values[k] !== s.detailValues[k]),
    [values, s.detailValues]
  );
  const set = (key) => (v) => setValues((prev) => ({ ...prev, [key]: v }));

  async function handleSave() {
    setSaving(true);
    setMsg(null);
    try {
      await updateStandDetail(s.standId, values);
      setMsg({ color: "success", text: t("standDetay.standDurumuKaydedildi") });
      onChanged();
    } catch (e) {
      setMsg({ color: "danger", text: e.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Row className="g-3">
      {/* #70: başlıktaki İnşa İzni / Taahhütname kareleri kaldırıldı, bilgi burada. */}
      <Col xs={12}>
        <InsaIzniCard s={s} />
      </Col>
      <Col xl={7}>
        <RibbonCard
          label={t("standDetay.standDurumu")} className="h-100"
          actions={(
            <div className="d-flex gap-2">
              {dirty && <Button color="light" size="sm" onClick={() => setValues(s.detailValues)} disabled={saving}>{t("standDetay.geriAl")}</Button>}
              <Button color="success" size="sm" onClick={handleSave} disabled={!dirty || saving}>
                {saving && <Spinner size="sm" className="me-1" />}{t("common:kaydet")}
              </Button>
            </div>
          )}
        >
          {msg && <Alert color={msg.color} className="py-2" toggle={() => setMsg(null)}>{msg.text}</Alert>}
          <Row>
            <Col md={6}>
              <div className="text-muted text-uppercase fs-11 mb-2">{t("standDetay.durum")}</div>
              {STAND_FLAGS.map((f) => (
                <SwitchRow key={f.key} id={`flag-${f.key}`} label={f.label} checked={values[f.key]} onChange={set(f.key)} danger={f.bad} disabled={saving} />
              ))}
            </Col>
            <Col md={6}>
              <div className="text-muted text-uppercase fs-11 mb-2">{t("standDetay.borcIsaretleri")}</div>
              {STAND_DEBT_FLAGS.map((f) => (
                <SwitchRow key={f.key} id={`debt-${f.key}`} label={t("standDetay.borcu", { label: f.label })} checked={values[f.key]} onChange={set(f.key)} danger disabled={saving} />
              ))}
              {s.internetBorcu && <div className="small text-danger fw-semibold">{t("standDetay.internetBorcuVar")}</div>}
            </Col>
          </Row>
          <div className="text-muted text-uppercase fs-11 mt-3 mb-2">{t("standDetay.teknikDegerler")}</div>
          <Row className="g-2">
            {STAND_TEXT_FIELDS.map((f) => (
              <Col sm={6} md={4} lg={3} key={f.key}>
                <Label size="sm" className="mb-0 pt-0 text-muted" for={`txt-${f.key}`}>{f.label}</Label>
                <Input id={`txt-${f.key}`} bsSize="sm" value={values[f.key]} disabled={saving} onChange={(e) => set(f.key)(e.target.value)} />
              </Col>
            ))}
          </Row>
        </RibbonCard>
      </Col>
      <Col xl={5}>
        <BorcCard s={s} onChanged={onChanged} />
      </Col>
    </Row>
  );
}

function BorcRow({ standId, cat, item, onSaved }) {
  const { t } = useTranslation("moduller");
  const [status, setStatus] = useState(item?.status ?? "yok");
  const [amount, setAmount] = useState(item?.amount ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const dirty = status !== (item?.status ?? "yok") || (status === "bekliyor" && String(amount) !== String(item?.amount ?? ""));
  const info = BORC_DURUMU_STATUS[item?.status ?? "yok"] ?? BORC_DURUMU_STATUS.yok;

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await setStandBorc(standId, cat.key, { status, amount });
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="py-2 border-bottom border-bottom-dashed">
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <div className="rounded-2 flex-shrink-0" style={{ width: 10, height: 34, background: info.hex }} />
        <div className="flex-grow-1" style={{ minWidth: 110 }}>
          <div className="fw-semibold small">{cat.label}</div>
          <div className="text-muted fs-12">
            {item?.status === "bekliyor" && item?.amount != null ? `${Number(item.amount).toLocaleString(currentLocaleTag())} ₺` : info.label}
          </div>
        </div>
        <Input type="select" bsSize="sm" style={{ width: 120 }} value={status} disabled={saving} onChange={(e) => setStatus(e.target.value)}>
          <option value="yok">{t("standDetay.borcYok")}</option>
          <option value="bekliyor">{t("standDetay.bekliyor")}</option>
          <option value="odendi">{t("standDetay.odendi")}</option>
        </Input>
        {status === "bekliyor" && (
          <Input
            type="number" min="0" step="0.01" bsSize="sm" placeholder="₺" style={{ width: 100 }}
            value={amount ?? ""} disabled={saving} onChange={(e) => setAmount(e.target.value)}
          />
        )}
        <Button color="success" size="sm" disabled={!dirty || saving} onClick={save}>
          {saving ? <Spinner size="sm" /> : t("common:kaydet")}
        </Button>
      </div>
      {error && <div className="text-danger small mt-1">{error}</div>}
    </div>
  );
}

function BorcCard({ s, onChanged }) {
  const { t } = useTranslation("moduller");
  const byKey = Object.fromEntries((s.borc.categories || []).map((c) => [c.categoryKey, c]));
  return (
    <RibbonCard label={t("standDetay.borcDurumu")} className="h-100">
      {BORC_KATEGORILERI.map((cat) => (
        <BorcRow
          // Kayıt değişince satır state'i taze başlasın.
          key={`${cat.key}-${byKey[cat.key]?.status}-${byKey[cat.key]?.amount}`}
          standId={s.standId} cat={cat} item={byKey[cat.key]} onSaved={onChanged}
        />
      ))}
      {s.legacyDebt && (
        <div className="small text-danger mt-2">
          <AlertTriangle size={13} className="me-1" />{t("standDetay.borcIsaretlerindenEnAzBiri")}
        </div>
      )}
    </RibbonCard>
  );
}

// ─── Talepler & İşler ───────────────────────────────────────────────────────
function TalepTable({ rows, catalog, emptyText }) {
  const { t } = useTranslation("moduller");
  const requested = (rows || []).filter((r) => r.hasRequest);
  if (requested.length === 0) return <div className="text-muted small">{emptyText}</div>;
  return (
    <div className="table-responsive">
      <Table size="sm" className="align-middle mb-0">
        <thead className="text-muted small"><tr><th>{t("standDetay.kategori")}</th><th>{t("standDetay.durum")}</th><th>{t("standDetay.tarih")}</th><th>{t("standDetay.aciklama")}</th><th>{t("standDetay.kaynak")}</th></tr></thead>
        <tbody>
          {requested.map((r, i) => {
            const st = TALEP_STATUS[r.status] ?? TALEP_STATUS[0];
            const label = catalog?.find((c) => c.key === r.categoryKey)?.label || r.categoryName || r.categoryKey || t("standDetay.talep");
            return (
              <tr key={r.requestId ?? `${r.categoryKey}-${i}`}>
                <td className="fw-semibold">{label}</td>
                <td><span className="badge text-white" style={{ background: st.hex }}>{st.label}</span></td>
                <td className="text-muted small text-nowrap">{r.startDate ? `${fmtDate(r.startDate)} – ${fmtDate(r.endDate)}` : "—"}</td>
                <td className="small">
                  {r.yapilacakIs || r.isOzeti || "—"}
                  {r.status === 2 && r.rejectReason && <div className="text-danger">{r.rejectReason}</div>}
                </td>
                <td><TalepKaynagi kaynak={r.kaynak} firmaAdi={r.kaynakFirmaAdi} kisa /></td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
}

function TaleplerTab({ s }) {
  const { t } = useTranslation("moduller");
  const [data, setData] = useState(null);
  useEffect(() => {
    let ignore = false;
    const safe = (p, fallback) => p.catch(() => fallback);
    Promise.all([
      safe(getTeknikTalepler(s.standId, { forceLive: true }), []),
      safe(getHizmetTalepleri(s.standId, { forceLive: true }), []),
      safe(getIsIzinleri(s.standId, { forceLive: true }), []),
      safe(getEkipmanTalebi(s.standId, { forceLive: true }), null),
    ]).then(([teknik, hizmet, isIzin, ekipman]) => {
      if (!ignore) setData({ teknik: teknik || [], hizmet: hizmet || [], isIzin: isIzin || [], ekipman });
    });
    return () => { ignore = true; };
  }, [s.standId]);

  if (!data) return <div className="text-center py-5"><Spinner color="primary" /></div>;
  const teknikByKey = Object.fromEntries(data.teknik.map((r) => [r.categoryKey, r]));

  return (
    <>
      <Row className="g-3 mb-3">
        <Col xl={4}>
          <RibbonCard label={t("standDetay.teknikTalepler")} className="h-100">
            <div className="d-flex flex-wrap gap-3">
              {TEKNIK_TALEP_KATEGORILERI.map((cat) => {
                const r = teknikByKey[cat.key];
                const on = !!r?.hasRequest;
                return (
                  <div key={cat.key} className="text-center" style={{ width: 64 }}>
                    <StatusTile
                      icon={TEKNIK_ICONS[cat.key]} background={on ? cat.color : NEUTRAL_GRAY} opacity={on ? 1 : 0.55}
                      label={on ? cat.label : t("standDetay.talepEdilmedi", { label: cat.label })} className="mx-auto"
                    />
                    <div className="small mt-1">{cat.label}</div>
                    <div className="text-muted fs-11">{on ? (r.quantity ? `${r.quantity.value} ${r.quantity.unit}` : t("standDetay.var")) : "—"}</div>
                    {on && <div className="mt-1"><TalepKaynagi kaynak={r.kaynak} firmaAdi={r.kaynakFirmaAdi} kisa /></div>}
                  </div>
                );
              })}
            </div>
          </RibbonCard>
        </Col>
        <Col xl={8}>
          <RibbonCard label={t("standDetay.hizmetTalepleri")} className="h-100">
            <TalepTable rows={data.hizmet} catalog={HIZMET_TALEP_KATEGORILERI} emptyText={t("standDetay.hizmetTalebiYok")} />
          </RibbonCard>
        </Col>
      </Row>
      <RibbonCard label={t("standDetay.isIzinleri")} className="mb-3">
        <TalepTable
          rows={[...data.isIzin, ...(data.ekipman ? [{ ...data.ekipman, categoryName: data.ekipman.categoryName || "Ekipman" }] : [])]}
          catalog={IS_IZNI_KATEGORILERI}
          emptyText={t("standDetay.isIzniYaDaEkipman")}
        />
      </RibbonCard>
      {s.jobs.length > 0 && (
        <RibbonCard label={t("standDetay.kayitliIsler")}>
          <div className="table-responsive">
            <Table size="sm" className="align-middle mb-0">
              <thead className="text-muted small"><tr><th>{t("standDetay.is")}</th><th>{t("standDetay.durum")}</th><th>{t("standDetay.calisan")}</th><th>{t("standDetay.denetci")}</th><th>{t("standDetay.tarih")}</th></tr></thead>
              <tbody>
                {s.jobs.map((j) => {
                  const st = TALEP_STATUS[j.status] ?? TALEP_STATUS[0];
                  return (
                    <tr key={j.id}>
                      <td className="fw-semibold">{j.typeName}</td>
                      <td><span className="badge text-white" style={{ background: st.hex }}>{st.label}</span></td>
                      <td>{j.worker || "—"}</td>
                      <td>{j.auditor || "—"}</td>
                      <td className="text-muted small text-nowrap">{fmtDate(j.start)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </RibbonCard>
      )}
      {/* #453: who changed what on this stand's requests (the staff request detail page, #75, is not built: this is where staff read it today) */}
      <RibbonCard label={t("standDetay.talepGecmisi")} className="mt-3">
        <TalepGecmisi standId={s.standId} />
      </RibbonCard>
    </>
  );
}

// ─── Künye düzenleme ────────────────────────────────────────────────────────
function KunyeModal({ s, onClose, onSaved }) {
  const { t } = useTranslation("moduller");
  const [form, setForm] = useState({
    standCode: s.standCode || standCodeFromName(s.standName) || "",
    salonId: s.salonId != null ? String(s.salonId) : "",
    tabelaAdi: s.tabelaAdi || "",
    tabelaAdiTouched: true,
  });
  const [salonOptions, setSalonOptions] = useState([]);
  const [salonsLoaded, setSalonsLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // #547: sunucu "bu holde bu adla stand var" dedi → Stand Kodu kutusu kırmızı, kod ya da hol değişince söner
  const [kodCakisti, setKodCakisti] = useState(false);

  useEffect(() => {
    let ignore = false;
    getSalonOptions(s.fuarId, { forceLive: true })
      .then((list) => { if (!ignore) { setSalonOptions(list || []); setSalonsLoaded(true); } });
    return () => { ignore = true; };
  }, [s.fuarId]);

  // Standın salonu fuarın listesinde olmayabilir (fuar 298'de gerçekten oluyor) — başa ekle.
  const options = useMemo(() => {
    if (s.salonId == null || salonOptions.some((o) => String(o.salonId) === String(s.salonId))) return salonOptions;
    return [{ salonId: s.salonId, salonName: s.salonName || `Salon ${s.salonId}` }, ...salonOptions];
  }, [salonOptions, s.salonId, s.salonName]);

  const kod = form.standCode.trim();
  const codeChanged = kod !== "" && kod !== (s.standCode || standCodeFromName(s.standName));
  const salonChanged = form.salonId !== "" && String(form.salonId) !== String(s.salonId ?? "");
  const tabelaChanged = form.tabelaAdi.trim() !== (s.tabelaAdi || "");

  async function save() {
    setSaving(true);
    setError(null);
    setKodCakisti(false);
    try {
      // Kısmi güncelleme — yalnız değişen anahtarlar. Salon değişirse ad sunucuda yeniden kurulur.
      await updateStand(s.standId, {
        standCode: codeChanged ? kod : undefined,
        salonId: salonChanged ? Number(form.salonId) : undefined,
        tabelaAdi: tabelaChanged ? form.tabelaAdi.trim() : undefined,
      }, { forceLive: true });
      onSaved();
    } catch (e) {
      setKodCakisti(isStandAdiCakismasi(e));
      setError(e?.response?.data?.message || e?.message || t("standDetay.standGuncellenemedi"));
      setSaving(false);
    }
  }

  return (
    <Modal isOpen toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>{t("standDetay.kunyeDuzenle")}{" "}{s.standName}</ModalHeader>
      <ModalBody>
        {error && <Alert color="danger" className="py-2">{error}</Alert>}
        <StandIdentityFields
          compact form={form}
          update={(patch) => {
            if ("standCode" in patch || "salonId" in patch) setKodCakisti(false);
            setForm((f) => ({ ...f, ...patch }));
          }}
          salonOptions={options} salonsUnavailable={salonsLoaded && options.length === 0} disabled={saving}
          codeInvalid={kodCakisti}
        />
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose}>{t("standDetay.vazgec")}</Button>
        <Button color="success" onClick={save} disabled={saving || !(codeChanged || salonChanged || tabelaChanged)}>
          {saving && <Spinner size="sm" className="me-1" />}{t("common:kaydet")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

/* WAS (until #439): KonumTab and its two helpers, moved to Components/Kroki/StandKonumKarti.jsx (mod="personel" is this behaviour).
// ─── Konum (backlog #78, #242) ────────────────────────────────────────────────
// Standın salon krokisi — fdsvector /embed/stand-plan, salt okunur. Üç görünüm (#242):
//   "Alan"           standın çevresi: standın kutusu Sistem Ayarları'ndaki Alan kenar payı
//                    kadar büyütülür, içindeki her şey kırpılarak gösterilir (around + margins);
//                    stand etiketli + vurgulu (selectedStandId)
//   "Salon"          tüm salon, açılışta Salon Planı çerçevesine + Salon kenar payına sığdırılır
//                    (fit=hall + margins); yalnızca bu stand etiketli + vurgulu, katman paneli yok
//   "Yalnızca Stand" yalnızca bu standın şekli, Stand kenar payıyla çerçevelenmiş (standIds + margins)
// Kenar payları: Sistem Ayarları › Plan Ayarları (#243, getKonumMargins); okunamazsa backend'in
// varsayılanlarıyla aynı yedek değerler kullanılır.
// WAS (#78): iki görünüm — "Salonda" {layers:"none", selectedStandId} ve "Yalnızca stand" {standIds}.
// Hepsi krokideki şeklin data.standId'si bu standın id'si olduğunda eşleşir. O bağ
// fdsvector'ün düzenleme modunda kurulur (şekli seç → Stand bölümü → standı bağla);
// bağlı şekil yoksa "Salonda" vurgusuz salonu, "Yalnızca stand" fdsvector'ün kendi
// "eşleşen nesne yok" notunu gösterir.
// Önce kroki yoklanır: çapraz kaynak iframe'in içindeki 404 ekranını okuyamayız
// (bkz. krokiVectorService.resolveStandPlanDocument, StandOverviewDetailPage).
// WAS (#78): [{ key: "salon", label: "Salonda" }, { key: "stand", label: "Yalnızca stand" }]
const KONUM_GORUNUMLERI = [
  { key: "alan", get label() { return i18n.t("moduller:standDetay.alan"); } },
  { key: "salon", get label() { return i18n.t("moduller:standDetay.salon"); } },
  { key: "stand", get label() { return i18n.t("moduller:standDetay.yalnizcaStand"); } },
];

// "sol 10 · üst 10 · sağ 10 · alt 10 m" — eşitse tek sayı.
function kenarPayiMetni(m) {
  if (!m) return "";
  const f = (v) => String(+Number(v).toFixed(2));
  const { left, top, right, bottom } = m;
  if (left === top && top === right && right === bottom) return i18n.t("moduller:standDetay.herYondeM", { f: f(left) });
  return i18n.t("moduller:standDetay.solUstSagAltM", { f: f(left), f2: f(top), f3: f(right), f4: f(bottom) });
}

// Backlog #180 (2026-09-26): fdsvector olayları (#179) — isolate-result, bu stand krokide eşleşmediyse
// sabit ipucu yerine "bu stand planda bağlı değil" der; stand-click tıklanan standın detayına
// götürür; saved stand verisini yeniden yükler. ⚠ fdsvector VITE_PARENT_ORIGIN ile derlenene kadar
// (backlog #184) hiçbiri gelmez — o zamana kadar sekme eskisi gibi sabit ipucuyla çalışır.
// WAS: function KonumTab({ s }) {
function KonumTab({ s, onChanged }) {
  const { t } = useTranslation("moduller");
  const navigate = useNavigate();
  // WAS (#78): useState("salon")
  const [gorunum, setGorunum] = useState("alan");
  // #242/#243: kenar payları Sistem Ayarları'ndan; bir kez okunur.
  const [kenarPaylari, setKenarPaylari] = useState(null);
  useEffect(() => {
    let ignore = false;
    getKonumMargins().then((m) => { if (!ignore) setKenarPaylari(m); });
    return () => { ignore = true; };
  }, []);
  // Hangi görünüm + stand için geldiyse onunla anahtarlanır — görünüm değişince eski sonuç düşer.
  const [isolateSonuc, setIsolateSonuc] = useState(null);
  const isolateKey = `${gorunum}|${s.standId}`;
  const eslesme = isolateSonuc?.key === isolateKey ? isolateSonuc.result : null;
  const bagliDegil = eslesme != null && (
    eslesme.matched === 0 || (eslesme.missingStandIds ?? []).map(String).includes(String(s.standId))
  );
  // Yoklama sonucu hangi fuar/salon çiftine aitse onunla anahtarlanır: çift değişince
  // eski sonuç kendiliğinden geçersiz sayılır — Effect içinde sıfırlamaya gerek yok.
  const probeKey = `${s.fuarId}|${s.salonId}`;
  const [probeSonuc, setProbeSonuc] = useState(null);
  const probe = probeSonuc?.key === probeKey ? probeSonuc.sonuc : null;

  useEffect(() => {
    if (!s.salonId) return undefined;
    let ignore = false;
    resolveStandPlanDocument({ fairId: s.fuarId, hallId: s.salonId })
      .then((sonuc) => { if (!ignore) setProbeSonuc({ key: probeKey, sonuc }); });
    return () => { ignore = true; };
  }, [probeKey, s.fuarId, s.salonId]);

  // WAS (#78): gorunum === "stand" ? { standIds: [s.standId] } : { layers: "none", selectedStandId: s.standId }
  const kenarPayi = kenarPaylari?.[gorunum];
  const viewOptions = gorunum === "stand"
    ? { standIds: [s.standId], margins: kenarPayi }
    : gorunum === "alan"
      ? { around: [s.standId], margins: kenarPayi, selectedStandId: s.standId }
      : { layers: "none", selectedStandId: s.standId, fit: "hall", margins: kenarPayi };

  let govde;
  if (!s.salonId) {
    govde = <div className="text-muted">{t("standDetay.standBirSalonaBagliDegil")}</div>;
  } else if (!probe || !kenarPaylari) {
    // Kenar payları gelmeden iframe açılmaz — yoksa önce payısız açılıp payla yeniden yüklenirdi.
    govde = (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <div className="text-muted small mt-2">{t("standDetay.krokiAraniyor")}</div>
      </div>
    );
  } else if (probe.reason === "not-mapped" || probe.reason === "missing-id") {
    govde = (
      <Alert color="warning" className="d-flex align-items-start gap-2 mb-0">
        <AlertTriangle size={18} className="flex-shrink-0 mt-1" />
        <div>
          {probe.reason === "missing-id"
            ? <>{t("standDetay.standinFuarSalonBilgisiEksik")}<code>{t("standDetay.fuarId")}{String(s.fuarId ?? "—")}</code> · <code>{t("standDetay.salonId")}{String(s.salonId ?? "—")}</code>).</>
            : <>{s.salonName || t("standDetay.buSalonun")}{" "}{t("standDetay.buFuarIcinKrokisiYok")}<code>{t("standDetay.fuarId")}{String(s.fuarId)}</code> · <code>{t("standDetay.salonId")}{String(s.salonId)}</code>).</>}
        </div>
      </Alert>
    );
  } else {
    // ok, ya da yoklama başarısız (ör. kroki oturumu henüz kurulmadı — iframe kendi
    // oturumunu kurar): krokiyi yine de aç, başarısızlığı üstte belirt.
    govde = (
      <>
        {!probe.ok && (
          <Alert color="warning" className="py-2 small">
            {t("standDetay.krokiOncedenDogrulanamadiGomuluUygulama", { message: probe.message })}
          </Alert>
        )}
        <div className="text-muted mb-2" style={{ fontSize: 11 }}>
          {t("standDetay.kenarPayi", { kenarPayiMetni: kenarPayiMetni(kenarPayi) })}
          {kenarPaylari.fromSettings
            ? t("standDetay.sistemAyarlariPlanAyarlari")
            : t("standDetay.varsayilanSistemAyarlariOkunamadi")}
        </div>
        <KrokiVectorEmbed
          fairId={s.fuarId}
          hallId={s.salonId}
          hallName={s.salonName}
          height={600}
          allowEdit={false}
          viewOptions={viewOptions}
          onIsolateResult={(result) => setIsolateSonuc({ key: isolateKey, result })}
          onStandClick={(e) => {
            if (e?.standId && String(e.standId) !== String(s.standId)) navigate(`/Management/Standlar/${e.standId}`);
          }}
          onSaved={() => onChanged?.()}
        />
        {bagliDegil ? (
          <Alert color="warning" className="py-2 small mt-2 mb-0">
            <strong>{s.standName}</strong>{" "}{t("standDetay.buPlandaBagliDegilKrokideki")}{" "}<strong>{t("standDetay.stand")}</strong>
            {t("standDetay.bolumundenBuStandiBaglayin")}
          </Alert>
        ) : (
          <div className="text-muted mt-2" style={{ fontSize: 11 }}>
            {t("standDetay.standKrokideGorunmuyorsaKrokiUygulamasinda")}{" "}<strong>{t("standDetay.stand")}</strong>{" "}{t("standDetay.bolumunden")}
            <strong> {s.standName}</strong>{" "}{t("standDetay.standiniBaglayin")}
          </div>
        )}
      </>
    );
  }

  return (
    <Card>
      <CardHeader className="py-2 d-flex align-items-center justify-content-between flex-wrap gap-2">
        <span className="fw-semibold">{s.salonName || t("standDetay.konum")}</span>
        {s.salonId && probe && probe.reason !== "not-mapped" && probe.reason !== "missing-id" && (
          <ButtonGroup size="sm">
            {KONUM_GORUNUMLERI.map((g) => (
              <Button
                key={g.key}
                color="primary"
                outline={gorunum !== g.key}
                onClick={() => setGorunum(g.key)}
              >
                {g.label}
              </Button>
            ))}
          </ButtonGroup>
        )}
      </CardHeader>
      <CardBody>{govde}</CardBody>
    </Card>
  );
}
*/

// ─── Sayfa ──────────────────────────────────────────────────────────────────
export default function StandDetayPage() {
  const { t: tx } = useTranslation("moduller");
  const { standId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = TABS.some((t) => t.key === searchParams.get("tab")) ? searchParams.get("tab") : "genel";

  const [stand, setStand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  // #550: the delete is a soft delete behind STAND_REQUEST_MANAGE (403 otherwise); there is no check endpoint for a stand.
  const kaldirabilir = useYetki(PERMISSION_KEYS.STAND_REQUEST_MANAGE) === true;

  useEffect(() => { document.title = i18n.t("moduller:standDetay.standDetayiFds"); }, []);

  useEffect(() => {
    let ignore = false;
    async function run() {
      setError(null);
      try {
        const data = await getStandDetay(standId);
        if (!ignore) setStand(data);
      } catch (e) {
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [standId, reloadToken]);

  const reload = () => setReloadToken((t) => t + 1);
  const setTab = (key) => setSearchParams(key === "genel" ? {} : { tab: key }, { replace: true });
  const goProject = () => navigate(`/Management/Projects/Stands/${standId}`, { state: { from: `${LIST_PATH}/${standId}` } });

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid className="text-center py-5"><Spinner color="primary" /></Container>
      </div>
    );
  }

  if (!stand) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={tx("standDetay.standDetayi")} pageTitle={tx("standDetay.standlar")} />
          <Alert color="danger">{error || tx("standDetay.standBulunamadi")}</Alert>
          <Button color="secondary" outline size="sm" onClick={() => navigate(LIST_PATH)}>
            <ArrowLeft size={14} className="me-1" />{tx("standDetay.standlar")}
          </Button>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={tx("standDetay.stand2", { standName: stand.standName })} pageTitle={tx("standDetay.standlar")} />

        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <Button color="secondary" outline size="sm" onClick={() => navigate(LIST_PATH)}>
            <ArrowLeft size={14} className="me-1" />{tx("standDetay.standlar")}
          </Button>
          <KontrolluSilDugmesi
            key={stand.standId} tur="stand" ad={stand.standName} izin={kaldirabilir}
            sil={() => standService.deleteStand(stand.standId)}
            onSilindi={() => navigate(LIST_PATH)}
          />
        </div>

        {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

        <StandHeader s={stand} activeTab={activeTab} onTab={setTab} onProject={goProject} />

        {activeTab === "genel" && <GenelTab s={stand} onEdit={() => setEditOpen(true)} onProject={goProject} />}
        {activeTab === "firmalar" && <FirmalarTab s={stand} onChanged={reload} />}
        {activeTab === "durum" && <DurumTab key={reloadToken} s={stand} onChanged={reload} />}
        {activeTab === "talepler" && <TaleplerTab s={stand} />}
        {/* WAS: <KonumTab s={stand} /> — #180: onChanged, fdsvector 'saved' olayında yeniden yükler. */}
        {/* #439: shared with the company portal; the staff behaviour is unchanged (mod="personel"). WAS: <KonumTab s={stand} onChanged={reload} /> */}
        {activeTab === "konum" && (
          <StandKonumKarti
            s={stand}
            onChanged={reload}
            onStandClick={(e) => {
              if (e?.standId && String(e.standId) !== String(stand.standId)) navigate(`/Management/Standlar/${e.standId}`);
            }}
          />
        )}

        {editOpen && (
          <KunyeModal s={stand} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); reload(); }} />
        )}
      </Container>
    </div>
  );
}
